import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { Appointment } from "../@data";
import {
  getAppointmentsByDate,
  getAppointmentsByClientAndDateRange,
  getAppointmentsByDateRange,
  getAppointmentsByProfessionalAndDate,
  createAppointment as createAppointmentService,
  updateAppointment as updateAppointmentService,
  deleteAppointment as deleteAppointmentService,
} from "../services/appointments";
import { useNotification } from "./notification";

export interface AppointmentsContextModel {
  appointments: Appointment[];
  loading: boolean;
  currentDate: string | null;
  fetchAppointmentsByDate: (date: string) => void;
  fetchAppointmentsByClientAndDateRange: (
    clientId: string,
    startDate: number,
    endDate: number
  ) => Promise<void>;
  fetchAppointmentsByDateRange: (
    startDate: number,
    endDate: number
  ) => Promise<void>;
  fetchAppointmentsByProfessionalAndDate: (
    professionalId: string,
    date: string
  ) => Promise<void>;
  createAppointment: (
    data: Omit<Appointment, "id" | "createdAt" | "updatedAt">
  ) => Promise<void>;
  updateAppointment: (
    id: string,
    data: Partial<Omit<Appointment, "id" | "createdAt" | "updatedAt">>
  ) => Promise<void>;
  deleteAppointment: (id: string) => Promise<void>;
}

const AppointmentsContext = createContext<AppointmentsContextModel>(
  {} as AppointmentsContextModel
);

export const AppointmentsProvider = ({ children }: { children: ReactNode }) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState<string | null>(null);
  const { handleNotification } = useNotification();

  const fetchAppointmentsByDate = useCallback((date: string) => {
    setLoading(true);
    setCurrentDate(date);
    getAppointmentsByDate(date)
      .then((data) => setAppointments(data))
      .catch((error: any) => {
        handleNotification("error", "Erro ao buscar agendamentos por data");
      })
      .finally(() => setLoading(false));
  }, []);

  const fetchAppointmentsByClientAndDateRange = useCallback(
    async (clientId: string, startDate: number, endDate: number) => {
      setLoading(true);
      try {
        const data = await getAppointmentsByClientAndDateRange(
          clientId,
          startDate,
          endDate
        );
        setAppointments(data);
      } catch (error: any) {
        handleNotification(
          "error",
          "Erro ao buscar agendamentos por cliente e data"
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const fetchAppointmentsByDateRange = useCallback(
    async (startDate: number, endDate: number) => {
      setLoading(true);
      try {
        const data = await getAppointmentsByDateRange(startDate, endDate);
        setAppointments(data);
      } catch (error: any) {
        handleNotification("error", "Erro ao buscar agendamentos por período");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const fetchAppointmentsByProfessionalAndDate = useCallback(
    async (professionalId: string, date: string) => {
      setLoading(true);
      try {
        const data = await getAppointmentsByProfessionalAndDate(
          professionalId,
          date
        );
        setAppointments(data);
      } catch (error: any) {
        handleNotification(
          "error",
          "Erro ao buscar agendamentos do profissional"
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const createAppointment = useCallback(
    async (data: Omit<Appointment, "id" | "createdAt" | "updatedAt">) => {
      setLoading(true);
      try {
        await createAppointmentService(data);
        // Recarrega apenas os agendamentos da data atual
        if (currentDate) {
          fetchAppointmentsByDate(currentDate);
        }
        handleNotification("success", "Agendamento criado com sucesso!");
      } catch (error: any) {
        handleNotification("error", "Erro ao criar agendamento");
      } finally {
        setLoading(false);
      }
    },
    [currentDate, fetchAppointmentsByDate]
  );

  const updateAppointment = useCallback(
    async (
      id: string,
      data: Partial<Omit<Appointment, "id" | "createdAt" | "updatedAt">>
    ) => {
      setLoading(true);
      try {
        await updateAppointmentService(id, data);
        // Recarrega apenas os agendamentos da data atual
        if (currentDate) {
          fetchAppointmentsByDate(currentDate);
        }
        handleNotification("success", "Agendamento atualizado com sucesso!");
      } catch (error: any) {
        handleNotification("error", "Erro ao atualizar agendamento");
      } finally {
        setLoading(false);
      }
    },
    [currentDate, fetchAppointmentsByDate]
  );

  const deleteAppointment = useCallback(
    async (id: string) => {
      setLoading(true);
      try {
        await deleteAppointmentService(id);
        if (currentDate) {
          fetchAppointmentsByDate(currentDate);
        }
        handleNotification("success", "Agendamento excluído com sucesso!");
      } catch (error: any) {
        handleNotification("error", "Erro ao excluir agendamento");
      } finally {
        setLoading(false);
      }
    },
    [currentDate, fetchAppointmentsByDate]
  );

  return (
    <AppointmentsContext.Provider
      value={{
        appointments,
        loading,
        currentDate,
        fetchAppointmentsByDate,
        fetchAppointmentsByClientAndDateRange,
        fetchAppointmentsByDateRange,
        fetchAppointmentsByProfessionalAndDate,
        createAppointment,
        updateAppointment,
        deleteAppointment,
      }}
    >
      {children}
    </AppointmentsContext.Provider>
  );
};

export const useAppointments = () => {
  const context = useContext(AppointmentsContext);
  if (!context) {
    throw new Error(
      "useAppointments deve ser usado dentro de um AppointmentsProvider"
    );
  }
  return context;
};
